import React, { useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/Sidebar';
import {
  getGuardianNavItemsWithAcademy,
  getCoachNavItemsWithAcademy,
  isOrganizationManagementRole,
  type ConsumerSidebarNavGroup,
  type SidebarNavGroup,
  type SidebarNavItem,
  type UnifiedConsumerNavStructure,
} from './sidebar-nav-config';
import { useUnifiedManagementNav } from '@/hooks/useUnifiedManagementNav';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { useCurrentAcademy } from '@/hooks/useDataStore';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useAuthStore } from '@/store/auth';
import { Building2, Check, Landmark } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { getDashboardHomePath } from '@/lib/dashboardBreadcrumbs';
import { cn } from '@/lib/utils';
import { TierUnlockBadge } from '@/components/tier/TierUnlockBadge';
import { getNavTierUnlockLevel } from '@/lib/tierUnlockBadges';

interface DashboardSidebarProps {
  dashboardRole:
    | 'academy_owner'
    | 'academy_admin'
    | 'club_owner'
    | 'club_admin'
    | 'guardian'
    | 'admin'
    | 'coach';
  academySlug?: string;
  academyName?: string;
  academyLogoUrl?: string | null;
  clubName?: string;
  clubLogoUrl?: string | null;
}

/** CourtSync mark cropped to fit the sidebar brand row (larger than nav icons). */
function CourtSyncBrandIcon({ className }: { className?: string }) {
  return (
    <span className={cn('relative size-6 shrink-0 overflow-hidden -ml-[3px]', className)} aria-hidden>
      <img
        src="/logo-gradient.png"
        alt=""
        className="absolute left-0 top-1/2 h-6 w-auto max-w-none -translate-y-1/2"
        width={126}
        height={32}
      />
    </span>
  );
}

const navLabelClassName =
  'truncate transition-opacity duration-300 ease-in-out group-data-[collapsible=icon]:opacity-0';

function navButtonClassName(isActive: boolean) {
  return cn(
    'hover:!bg-transparent active:!bg-transparent',
    isActive
      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
  );
}

interface ManagementNavMenuProps {
  items: SidebarNavItem[];
  isActiveRoute: (href: string) => boolean;
  onNavigate: (to: string) => void;
}

function ManagementNavMenu({ items, isActiveRoute, onNavigate }: ManagementNavMenuProps) {
  return (
    <SidebarMenu className="gap-0.5 px-2 py-2">
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = isActiveRoute(item.to);
        return (
          <SidebarMenuItem key={item.to}>
            <SidebarMenuButton
              {...(item.tourId ? { id: item.tourId } : {})}
              onClick={() => onNavigate(item.to)}
              tooltip={item.label}
              className={cn(navButtonClassName(isActive), 'relative')}
              title={item.label}
            >
              <Icon className="size-4 shrink-0" />
              <span className={navLabelClassName}>{item.label}</span>
              <TierUnlockBadge unlockTier={getNavTierUnlockLevel(item.to)} />
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

interface ManagementNavGroupSectionProps {
  group: SidebarNavGroup | ConsumerSidebarNavGroup;
  isActiveRoute: (href: string) => boolean;
  onNavigate: (to: string) => void;
}

function ManagementNavGroupSection({
  group,
  isActiveRoute,
  onNavigate,
}: ManagementNavGroupSectionProps) {
  const groupTourId =
    group.id === 'facility'
      ? 'club-nav-sections'
      : group.id === 'club'
        ? 'club-nav-group'
        : group.id === 'lessons'
          ? 'consumer-nav-lessons'
          : undefined;

  return (
    <SidebarGroup className="px-0 py-0">
      <SidebarGroupLabel
        id={groupTourId}
        className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {group.label}
      </SidebarGroupLabel>
      <SidebarGroupContent className="px-0">
        <SidebarMenu className="gap-0.5 px-2 pb-2">
          {group.items.map((item) => {
            const Icon = item.icon;
            const isActive = isActiveRoute(item.to);
            return (
              <SidebarMenuItem key={item.to}>
                <SidebarMenuButton
                  {...(item.tourId ? { id: item.tourId } : {})}
                  onClick={() => onNavigate(item.to)}
                  tooltip={item.label}
                  className={cn(navButtonClassName(isActive), 'relative')}
                  title={item.label}
                >
                  <Icon className="size-4 shrink-0" />
                  <span className={navLabelClassName}>{item.label}</span>
                  <TierUnlockBadge unlockTier={getNavTierUnlockLevel(item.to)} />
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export function DashboardSidebar({
  dashboardRole,
  academySlug,
  academyName,
  academyLogoUrl,
  clubName,
  clubLogoUrl,
}: DashboardSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, setOpenMobile, state: sidebarState } = useSidebar();
  const { user } = useAuthStore();
  const { currentAcademy, userAcademies, setCurrentAcademy } = useCurrentAcademy();
  const { currentClub, userClubs, setCurrentClub } = useCurrentClub();

  const closeMobileSidebar = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const guardianAcademy = useGuardianAcademyStore((s) => s.academy);
  const coachAcademy = useCoachAcademyStore((s) => s.academy);
  const academyForPaths =
    dashboardRole === 'guardian' ? guardianAcademy : dashboardRole === 'coach' ? coachAcademy : null;

  const resolvedAcademyName =
    academyName ??
    (dashboardRole === 'guardian'
      ? guardianAcademy?.name
      : dashboardRole === 'coach'
        ? coachAcademy?.name
        : currentAcademy?.name);

  const resolvedAcademyLogoUrl =
    academyLogoUrl ??
    (dashboardRole === 'guardian'
      ? guardianAcademy?.logoUrl
      : dashboardRole === 'coach'
        ? coachAcademy?.logoUrl
        : undefined);

  const unifiedNav = useUnifiedManagementNav(dashboardRole);
  const consumerNav = useUnifiedConsumerNav();

  const navItems = useMemo(() => {
    if (dashboardRole === 'guardian' && consumerNav.useUnifiedNav) {
      return consumerNav.navItems;
    }
    const navTarget = academyForPaths ?? academySlug;
    if (dashboardRole === 'guardian' && navTarget) {
      return getGuardianNavItemsWithAcademy(navTarget);
    }
    if (dashboardRole === 'coach' && navTarget) {
      return getCoachNavItemsWithAcademy(navTarget);
    }
    if (isOrganizationManagementRole(dashboardRole)) {
      return unifiedNav.navItems;
    }
    return unifiedNav.navItems;
  }, [
    dashboardRole,
    academySlug,
    academyForPaths,
    unifiedNav.navItems,
    consumerNav.useUnifiedNav,
    consumerNav.navItems,
  ]);

  const managementNavStructure = isOrganizationManagementRole(dashboardRole)
    ? unifiedNav.navStructure
    : null;

  const consumerNavStructure: UnifiedConsumerNavStructure | null =
    dashboardRole === 'guardian' && consumerNav.useUnifiedNav ? consumerNav.navStructure : null;

  const isActiveRoute = (href: string) => {
    if (href === '/admin/dashboard') {
      return (
        location.pathname === href ||
        location.pathname === '/dashboard' ||
        location.pathname === '/admin/dashboard'
      );
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isAcademyBranded = dashboardRole === 'guardian' || dashboardRole === 'coach';
  const preferClubBrand =
    dashboardRole === 'guardian' &&
    consumerNav.useUnifiedNav &&
    !consumerNav.hasLessonsAccess &&
    consumerNav.hasClubMemberAccess;
  const showAcademySelector =
    unifiedNav.showAcademySelector && userAcademies.length > 1;
  const showClubSelector = unifiedNav.showClubSelector && userClubs.length > 1;

  const brandTitle =
    dashboardRole === 'admin'
      ? 'Backoffice'
      : preferClubBrand
        ? clubName || 'Club'
        : isAcademyBranded
          ? resolvedAcademyName || 'Academy'
          : 'CourtSync';

  const brandHomePath = getDashboardHomePath(dashboardRole, academySlug);

  const handleNavNavigate = (to: string) => {
    navigate(to);
    closeMobileSidebar();
  };

  const renderManagementNav = () => {
    const useFlatNav =
      managementNavStructure?.type === 'grouped' && sidebarState === 'collapsed' && !isMobile;

    if (managementNavStructure?.type === 'grouped' && !useFlatNav) {
      return (
        <>
          <ManagementNavMenu
            items={managementNavStructure.leading}
            isActiveRoute={isActiveRoute}
            onNavigate={handleNavNavigate}
          />
          {managementNavStructure.groups.map((group) => (
            <ManagementNavGroupSection
              key={group.id}
              group={group}
              isActiveRoute={isActiveRoute}
              onNavigate={handleNavNavigate}
            />
          ))}
          {managementNavStructure.trailing.length > 0 ? (
            <ManagementNavMenu
              items={managementNavStructure.trailing}
              isActiveRoute={isActiveRoute}
              onNavigate={handleNavNavigate}
            />
          ) : null}
        </>
      );
    }

    return (
      <ManagementNavMenu
        items={navItems}
        isActiveRoute={isActiveRoute}
        onNavigate={handleNavNavigate}
      />
    );
  };

  const renderConsumerNav = () => {
    const useFlatNav =
      consumerNavStructure?.type === 'grouped' && sidebarState === 'collapsed' && !isMobile;

    if (consumerNavStructure?.type === 'grouped' && !useFlatNav) {
      return (
        <>
          <ManagementNavMenu
            items={consumerNavStructure.leading}
            isActiveRoute={isActiveRoute}
            onNavigate={handleNavNavigate}
          />
          {consumerNavStructure.groups.map((group) => (
            <ManagementNavGroupSection
              key={group.id}
              group={group}
              isActiveRoute={isActiveRoute}
              onNavigate={handleNavNavigate}
            />
          ))}
          {consumerNavStructure.trailing.length > 0 ? (
            <ManagementNavMenu
              items={consumerNavStructure.trailing}
              isActiveRoute={isActiveRoute}
              onNavigate={handleNavNavigate}
            />
          ) : null}
        </>
      );
    }

    return (
      <ManagementNavMenu
        items={navItems}
        isActiveRoute={isActiveRoute}
        onNavigate={handleNavNavigate}
      />
    );
  };

  const brandIcon =
    preferClubBrand && clubLogoUrl ? (
      <img
        src={clubLogoUrl}
        alt=""
        className="size-6 shrink-0 -ml-[3px] rounded object-contain"
        referrerPolicy="no-referrer"
        aria-hidden
      />
    ) : preferClubBrand ? (
      <Building2 className="size-6 shrink-0 -ml-[3px] text-primary-600 dark:text-primary-400" aria-hidden />
    ) : isAcademyBranded && resolvedAcademyLogoUrl ? (
      <img
        src={resolvedAcademyLogoUrl}
        alt=""
        className="size-6 shrink-0 -ml-[3px] rounded object-contain"
        referrerPolicy="no-referrer"
        aria-hidden
      />
    ) : isAcademyBranded ? (
      <Building2 className="size-6 shrink-0 -ml-[3px] text-primary-600 dark:text-primary-400" aria-hidden />
    ) : (
      <CourtSyncBrandIcon />
    );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="gap-0 p-0">
        <SidebarMenu className="gap-0.5 px-2 py-2">
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={brandTitle}
              className={cn(navButtonClassName(false), 'font-semibold')}
            >
              <Link to={brandHomePath} aria-label={brandTitle}>
                {brandIcon}
                <span className={navLabelClassName}>{brandTitle}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {showAcademySelector && user ? (
          <>
            <div className="flex min-w-0 items-center gap-2 px-2 pb-2 group-data-[collapsible=icon]:hidden">
              <Building2 className="size-4 shrink-0 text-muted-foreground" />
              <select
                aria-label="Select academy"
                value={currentAcademy?.id ?? ''}
                onChange={(e) => {
                  const academy = userAcademies.find((a) => a.id === e.target.value);
                  if (academy) setCurrentAcademy(academy);
                }}
                className="input h-8 w-full min-w-0 text-sm"
              >
                {userAcademies.map((academy) => (
                  <option key={academy.id} value={academy.id}>
                    {academy.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="hidden px-2 pb-2 group-data-[collapsible=icon]:block">
              <SidebarMenu>
                <SidebarMenuItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <SidebarMenuButton
                        tooltip={currentAcademy?.name ?? 'Select academy'}
                        className={navButtonClassName(false)}
                        aria-label="Select academy"
                      >
                        <Building2 className="size-4 shrink-0" />
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="start" className="min-w-[12rem]">
                      {userAcademies.map((academy) => (
                        <DropdownMenuItem
                          key={academy.id}
                          onClick={() => setCurrentAcademy(academy)}
                          className="flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{academy.name}</span>
                          {academy.id === currentAcademy?.id ? (
                            <Check className="size-4 shrink-0" aria-hidden />
                          ) : null}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              </SidebarMenu>
            </div>
          </>
        ) : null}

        {showClubSelector ? (
          <>
            <div className="flex min-w-0 items-center gap-2 px-2 pb-2 group-data-[collapsible=icon]:hidden">
              <Landmark className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <select
                aria-label="Select club"
                value={currentClub?.id ?? ''}
                onChange={(e) => {
                  const club = userClubs.find((c) => c.id === e.target.value);
                  if (club) setCurrentClub(club);
                }}
                className="input h-8 w-full min-w-0 text-sm"
              >
                {userClubs.map((club) => (
                  <option key={club.id} value={club.id}>
                    {club.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="hidden px-2 pb-2 group-data-[collapsible=icon]:block">
              <SidebarMenu>
                <SidebarMenuItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <SidebarMenuButton
                        tooltip={currentClub?.name ?? 'Select club'}
                        className={navButtonClassName(false)}
                        aria-label="Select club"
                      >
                        <Landmark className="size-4 shrink-0" aria-hidden />
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="start" className="min-w-[12rem]">
                      {userClubs.map((club) => (
                        <DropdownMenuItem
                          key={club.id}
                          onClick={() => setCurrentClub(club)}
                          className="flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{club.name}</span>
                          {club.id === currentClub?.id ? (
                            <Check className="size-4 shrink-0" aria-hidden />
                          ) : null}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              </SidebarMenu>
            </div>
          </>
        ) : null}
      </SidebarHeader>

      <SidebarContent
        id={
          isOrganizationManagementRole(dashboardRole)
            ? 'management-tools'
            : dashboardRole === 'guardian'
              ? TOUR_STEP_IDS.STUDENT_NAV
              : undefined
        }
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pt-0"
      >
        {isOrganizationManagementRole(dashboardRole) ? (
          renderManagementNav()
        ) : consumerNavStructure ? (
          renderConsumerNav()
        ) : (
          <ManagementNavMenu
            items={navItems}
            isActiveRoute={isActiveRoute}
            onNavigate={handleNavNavigate}
          />
        )}
      </SidebarContent>

      <SidebarFooter className="mt-auto shrink-0 border-t border-sidebar-border/40 p-0">
        <div className="flex w-full items-center px-2 py-2">
          <SidebarTrigger
            className="hidden size-8 shrink-0 rounded-md text-gray-600 transition-colors duration-300 ease-in-out hover:bg-transparent hover:text-gray-900 dark:text-gray-400 dark:hover:bg-transparent dark:hover:text-gray-200 md:inline-flex"
            aria-label="Toggle sidebar"
          />
          <div
            className={cn(
              'ml-auto flex min-w-0 items-center justify-end gap-1.5 overflow-hidden',
              'transition-[opacity,max-width] duration-300 ease-in-out',
              'group-data-[collapsible=icon]:max-w-0 group-data-[collapsible=icon]:opacity-0'
            )}
          >
            {isAcademyBranded ? (
              <>
                <img src="/logo-gradient.png" alt="CourtSync" className="h-4 w-auto shrink-0 opacity-70" />
                <span className="truncate whitespace-nowrap text-[9px] text-gray-500 dark:text-gray-400">
                  Powered by CourtSync
                </span>
                <span className="shrink-0 whitespace-nowrap text-[10px] text-gray-400 dark:text-gray-500">
                  v{__APP_VERSION__}
                </span>
              </>
            ) : (
              <span className="shrink-0 whitespace-nowrap text-[10px] text-gray-400 dark:text-gray-500">
                v{__APP_VERSION__}
              </span>
            )}
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
